import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCookiePrefs } from "@/components/CookiePrefsContext";
import Logo from "@/components/Logo";

export default function Footer() {
  const { t } = useTranslation();
  const { lang = "hr" } = useParams();
  const { openManager } = useCookiePrefs();
  const base = `/${lang}`;

  const emailAddress = t("footer.legal.email");
  const phone = t("footer.legal.phone");

  return (
    <footer
      data-testid="site-footer"
      className="mt-24 border-t border-border bg-card"
    >
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Logo className="h-9 text-foreground" />
            <p className="mt-4 text-sm text-muted-foreground max-w-md">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 space-y-1 text-xs text-muted-foreground font-mono-plex">
              <div>
                <span className="text-foreground">{t("footer.legal.company_name")}</span>
              </div>
              <div>{t("footer.legal.address")}</div>
              <div>
                {t("footer.legal.oib_label")}: {t("footer.legal.oib")} ·{" "}
                {t("footer.legal.mbs_label")}: {t("footer.legal.mbs")}
              </div>
              <div>{t("footer.legal.court")}</div>
              <div>
                {t("footer.legal.capital_label")}: {t("footer.legal.capital")}
              </div>
              <div>{t("footer.legal.supervision")}</div>
              <div>{t("footer.legal.licence")}</div>
              <div>{t("footer.legal.jurisdiction")}</div>
              <div>
                {t("footer.legal.incorporated_label")}: {t("footer.legal.incorporated")}
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-mono-plex">
              {t("footer.nav_title")}
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              {["about", "how", "trust", "contact"].map((k) => (
                <li key={k}>
                  <a
                    href={`${base}#${k}`}
                    className="text-foreground hover:text-primary transition-colors duration-150"
                    data-testid={`footer-nav-${k}`}
                  >
                    {t(`nav.${k}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-mono-plex">
              {t("footer.legal_title")}
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={`${base}/privacy`}
                  className="text-foreground hover:text-primary transition-colors"
                  data-testid="footer-privacy-link"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${base}/cookies`}
                  className="text-foreground hover:text-primary transition-colors"
                  data-testid="footer-cookies-link"
                >
                  {t("footer.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${base}/terms`}
                  className="text-foreground hover:text-primary transition-colors"
                  data-testid="footer-terms-link"
                >
                  {t("footer.terms")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openManager}
                  className="text-foreground hover:text-primary transition-colors underline underline-offset-4 decoration-border"
                  data-testid="footer-cookie-settings"
                >
                  {t("footer.cookie_settings")}
                </button>
              </li>
            </ul>

            <h4 className="mt-8 text-xs uppercase tracking-[0.14em] text-muted-foreground font-mono-plex">
              {t("footer.contact_title")}
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a
                  href={`mailto:${emailAddress}`}
                  className="text-foreground hover:text-primary transition-colors"
                  data-testid="footer-email-link"
                >
                  {emailAddress}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="text-foreground hover:text-primary transition-colors"
                  data-testid="footer-phone-link"
                >
                  {phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>{t("footer.legal.copyright")}</span>
          <span className="font-mono-plex tracking-[0.14em] uppercase">arqovena.com</span>
        </div>
      </div>
    </footer>
  );
}
